import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FormControl, styled, Typography } from '@mui/material';
import _ from 'lodash';
import { FolderView } from 'api/models/FolderView';
import { CREATING_SUBFOLDER, SUBTITLES_FOR_DIALOGS } from 'common/constants/documents';
import { CustomTextField } from 'components';
import { FormDialog } from 'components/Dialogs';
import { DocumentsContext } from 'context/DocumentsContext';
import { InformationDashboard409AContext } from 'context/InformationDashboard409AContext';
import { useDocuments } from 'services/hooks';
import { useFormValidation } from 'services/hooks/useFormValidation';
import { CustomFormValues } from 'services/hooks/useFormValidation/types';
import { Logger } from 'utilities';
import CustomAutoSelectFolder from './CustomAutoSelectFolder/CustomAutoSelectFolder';
import { AddFolderDialogProps, FolderOption, FormState } from './types';

const constraints = {
  foldername: {
    presence: { allowEmpty: false, message: 'is required' },
    length: { minimum: 3, message: 'must be at least 3 characters' },
  },
};

const initialValues: FormState<CustomFormValues> = {
  values: {
    parentFolderId: null,
    foldername: '',
  },
  errors: {},
  isValid: false,
};

const ERROR_MESSAGES = {
  DUPLICATE_WITH_PARENT: 'A folder with this name already exists in the selected parent folder.',
  DUPLICATE_WITHOUT_PARENT: 'A folder with this name already exists at the root level.',
};

const StyledTextField = styled(CustomTextField)({
  marginTop: '1rem',
});

const StyledLabel = styled(Typography)({
  marginBottom: '0.5rem',
});

const AddFolderDialog = ({
  open,
  onClose,
  title,
  currentFolders,
  getNewFolder,
  currentDocuments,
}: AddFolderDialogProps) => {
  const { createSubFolder } = useDocuments();
  const [formState, setFormState] = useState<FormState<CustomFormValues>>(initialValues);
  const [isDuplicateName, setIsDuplicateName] = useState(false);
  const [duplicateNameMessage, setDuplicateNameMessage] = useState<string | null>(null);
  const isCreatingFolder = useRef(false);
  const loadingText = useRef<string | null>(null);

  const documentsContext = useContext(DocumentsContext);
  const informationDashboardContext = useContext(InformationDashboard409AContext);
  // This is because the modal can be called from the Reference Widget, Documents page or Information page (Company Dashboard)
  const refreshDocuments = documentsContext?.refreshDocuments ?? informationDashboardContext?.refreshDocuments;

  if (!refreshDocuments) {
    Logger.error('Failed to obtain refreshDocuments from context.', {
      documentsContext,
      informationDashboardContext,
    });
  }

  const { formValidated, validateForm } = useFormValidation(constraints);
  const values = formValidated.values as CustomFormValues;

  const updatedCurrentFolders: FolderOption[] = [{ id: 0, name: 'No Option' }].concat(
    currentFolders
      .filter(folder => folder.id !== undefined)
      .map(folder => ({
        id: folder.id as number,
        name: folder.name,
      }))
  );

  useEffect(() => {
    setFormState(formValidated);
  }, [formValidated]);

  const validateDuplicatedNames = useCallback(
    (trimmedValue: string) => {
      const parentFolders = currentDocuments?.folders.filter(folder => folder.parent_folder === null);
      const subFolders = currentDocuments?.folders.filter(folder => folder.parent_folder !== null);

      let isDuplicate = false;
      let errorMessage = null;

      if (_.isNil(values.parentFolderId)) {
        isDuplicate = parentFolders!.some(folder => folder.name.toLowerCase() === trimmedValue);
        if (isDuplicate) {
          errorMessage = ERROR_MESSAGES.DUPLICATE_WITHOUT_PARENT;
        }
      } else {
        isDuplicate = subFolders!.some(
          folder => folder.parent_folder?.id === values.parentFolderId && folder.name.toLowerCase() === trimmedValue
        );
        if (isDuplicate) {
          errorMessage = ERROR_MESSAGES.DUPLICATE_WITH_PARENT;
        }
      }

      setIsDuplicateName(isDuplicate);
      setDuplicateNameMessage(errorMessage);
    },
    [values, currentDocuments]
  );

  useEffect(() => {
    const trimmedValue = values.foldername?.trim().toLowerCase();
    if (trimmedValue) {
      validateDuplicatedNames(trimmedValue);
    } else {
      setIsDuplicateName(false);
      setDuplicateNameMessage(null);
    }
  }, [values, validateDuplicatedNames]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();
    const { name, value } = event.target;

    validateForm({
      ...values,
      [name]: value,
    });
  };

  const handleParentFolderChange = (id: string) => {
    const parentFolderId = Number(id) === 0 ? null : Number(id);

    validateForm({
      ...values,
      parentFolderId,
    });
  };

  const handleSave = useCallback(async () => {
    const folderData = {
      md_documents_id: currentDocuments?.id,
      parent_folder_id: Number(values.parentFolderId) || null,
      name: values.foldername ?? null,
    };

    loadingText.current = CREATING_SUBFOLDER;
    isCreatingFolder.current = true;

    try {
      if (!currentDocuments?.id) {
        return;
      }
      const response = await createSubFolder(currentDocuments.id, folderData as { name: string });
      if (getNewFolder && response) {
        getNewFolder(response as FolderView);
      }
      validateForm(initialValues.values);
      if (refreshDocuments) {
        refreshDocuments();
      }
    } catch (error) {
      Logger.error('Error creating subfolder', error);
    } finally {
      isCreatingFolder.current = false;
      loadingText.current = null;
    }
  }, [currentDocuments?.id, createSubFolder, getNewFolder, values, validateForm, refreshDocuments]);

  const handleOnClose = () => {
    validateForm(initialValues.values);
    onClose();
  };

  return (
    <FormDialog
      open={open}
      title={title}
      customButtonLabel="Done"
      onSave={handleSave}
      onClose={handleOnClose}
      isValid={formState.isValid && !isDuplicateName && !isCreatingFolder.current}
      isLoading={isCreatingFolder.current}
      loadingText={loadingText.current ?? undefined}>
      <>
        <FormControl fullWidth>
          {currentFolders && updatedCurrentFolders && (
            <>
              <StyledLabel variant="h3">Parent Folder</StyledLabel>
              <CustomAutoSelectFolder
                inputProps={{
                  id: 'parent-folder',
                  name: 'parent-folder',
                  label: 'Select an existing folder',
                }}
                data={updatedCurrentFolders}
                onChange={handleParentFolderChange}
              />
            </>
          )}
          <StyledTextField
            id="folder-name"
            name="foldername"
            type="text"
            label="Name"
            required
            fullWidth
            onChange={handleChange}
            value={values.foldername ?? ''}
            error={isDuplicateName}
            helperText={duplicateNameMessage}
            variant="standard"
          />
        </FormControl>
        <Typography variant="caption" style={{ margin: '0.5rem 0', color: 'darkgrey' }}>
          {SUBTITLES_FOR_DIALOGS('folder')}
        </Typography>
      </>
    </FormDialog>
  );
};

export default AddFolderDialog;
